import type { MarketPort } from "../domain/market";

type Submission = { path: string; boc: string };
const validPath = /^\/(swaps|liquidity|slots)\/[a-zA-Z0-9_-]{1,100}\/submit$/;
const memory = new Map<string, Submission[]>();
/** Signed public wallet responses only; never keys. Bound to the authenticated canonical user. */
export class MarketSubmissionJournal {
  readonly #key: string;
  readonly #storage: Pick<Storage, "getItem" | "setItem">;
  #pending: Submission[];
  #flushing = false;
  constructor(userId: string, storage?: Pick<Storage, "getItem" | "setItem">) {
    this.#key = `diamond-tapper:market-submissions:v1:${userId}`;
    try { this.#storage = storage ?? window.localStorage; }
    catch { this.#storage = { getItem: () => null, setItem: () => undefined }; }
    let stored: Submission[] = [];
    try {
      const value: unknown = JSON.parse(this.#storage.getItem(this.#key) ?? "[]");
      if (Array.isArray(value)) stored = value.filter((entry): entry is Submission => {
        if (!entry || typeof entry !== "object") return false;
        const item = entry as Partial<Submission>;
        return typeof item.path === "string" && validPath.test(item.path) && typeof item.boc === "string" && item.boc.length >= 32 && item.boc.length <= 65536;
      });
    } catch { /* Storage may be unavailable; same-session memory remains useful. */ }
    this.#pending = [...new Map([...stored, ...(memory.get(this.#key) ?? [])].map(entry => [entry.path, entry])).values()];
  }
  get size() { return this.#pending.length; }
  has(path: string) { return this.#pending.some(item => item.path === path); }
  #save() {
    memory.set(this.#key, this.#pending);
    try { this.#storage.setItem(this.#key, JSON.stringify(this.#pending)); } catch { /* Never turn an already signed payment into a retry-payment instruction. */ }
  }
  add(path: string, boc: string) {
    if (!validPath.test(path) || boc.length < 32 || boc.length > 65536) throw new Error("market_submission_invalid");
    if (!this.#pending.some(item => item.path === path)) this.#pending.push({ path, boc });
    this.#save();
  }
  async flush(api: MarketPort) {
    if (this.#flushing) return;
    this.#flushing = true;
    try {
      for (const item of [...this.#pending]) {
        try {
          await api.request(item.path, { boc: item.boc });
          this.#pending = this.#pending.filter(entry => entry.path !== item.path); this.#save();
        } catch { /* Retry the receipt, never the financial action, on the next foreground sync. */ }
      }
    } finally { this.#flushing = false; }
  }
}
